<template>
  <div class="main-request-detail" v-loading="loading">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <el-button link @click="goBack">
          <el-icon><ArrowLeft /></el-icon>
          목록으로
        </el-button>
        <h2>{{ mainRequest?.request_number }}</h2>
        <el-tag :type="getStatusType(mainRequest?.status)" size="large">
          {{ mainRequest?.status_display }}
        </el-tag>
      </div>
      <div class="header-right">
        <el-button
          v-if="userStore.isMainRequestManager"
          type="primary"
          @click="openEditDialog"
        >
          수정
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 스타일 요약 -->
    <div v-if="mainRequest" class="style-summary">
      <div class="style-image">
        <el-image
          v-if="headerImage"
          :src="headerImage"
          :preview-src-list="headerImages"
          fit="contain"
          class="style-thumb"
        >
          <template #error>
            <div class="style-thumb-placeholder">
              <el-icon :size="28"><Picture /></el-icon>
            </div>
          </template>
        </el-image>
        <div v-else class="style-thumb-placeholder">
          <el-icon :size="28"><Picture /></el-icon>
        </div>
      </div>
      <div class="style-info">
        <div class="style-number-row">
          <span class="style-number">{{ mainRequest.design_style_number }}</span>
          <el-tag size="small" type="info">{{ mainRequest.design_internal_number }}</el-tag>
        </div>
        <div class="style-meta">
          <span v-if="mainRequest.design_product_name">{{ mainRequest.design_product_name }}</span>
          <span class="meta-sep">|</span>
          <span>{{ mainRequest.brand_name }}</span>
          <template v-if="mainRequest.design_year || mainRequest.design_season">
            <span class="meta-sep">|</span>
            <span>{{ mainRequest.design_year || '' }} {{ mainRequest.design_season || '' }}</span>
          </template>
          <template v-if="mainRequest.manufacturer_name">
            <span class="meta-sep">|</span>
            <span>{{ mainRequest.manufacturer_name }}</span>
          </template>
        </div>
      </div>

      <!-- 핵심 지표 — 기존 요약 카드 6개를 배너 우측 스탯으로 통합
           (상태=헤더 태그, 브랜드=배너 메타와 중복이라 제거. 세로 ~160px 회수,
           모든 탭에서 첫 화면에 실제 내용이 보이도록. 2026-08-01) -->
      <div class="style-stats">
        <div class="stat">
          <span class="stat-label">납기일</span>
          <span class="stat-value">
            {{ mainRequest?.delivery_date }}
            <el-tag :type="getDdayType(mainRequest?.days_until_delivery)" size="small" class="dday-tag">
              {{ formatDday(mainRequest?.days_until_delivery) }}
            </el-tag>
          </span>
        </div>
        <div class="stat">
          <span class="stat-label">발주 / 생산수량</span>
          <span class="stat-value">
            {{ formatNumber(mainRequest?.order_quantity) }}
            <span v-if="mainRequest?.allowance_rate > 0" class="production-qty-sub">
              / {{ formatNumber(mainRequest?.production_quantity) }}
              <span class="allowance-badge">(+{{ mainRequest.allowance_rate }}%)</span>
            </span>
          </span>
        </div>
        <div v-if="userStore.hasCostAccess" class="stat">
          <span class="stat-label">총 원가</span>
          <span class="stat-value">{{ formatCurrency(mainRequest?.total_cost) }}</span>
        </div>
        <div v-if="userStore.hasCostAccess" class="stat">
          <span class="stat-label">단가</span>
          <span class="stat-value">{{ formatCurrency(mainRequest?.unit_cost) }}</span>
        </div>
      </div>
    </div>

    <!-- 생산 파이프라인 -->
    <ProductionPipeline
      v-if="mainRequest?.pipeline_stages"
      :stages="mainRequest.pipeline_stages.stages"
      :compact="false"
      @select-stage="handleStageClick"
    />

    <!-- 탭 영역 -->
    <el-card class="content-card">
      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <!-- 탭 순서 = 업무 순서 (2026-08-01, 사이먼):
             입력(요척 → 수량배분 → 그레이딩 → 자재현황) → 문서(생산지시서)
             → 생산 진행(임가공·마일스톤·현장보고·이슈) → 후반(선적·원가·참여자) -->
        <el-tab-pane label="개요" name="overview">
          <OverviewTab
            :main-request="mainRequest"
            :participants="participants"
            :can-edit="userStore.isMainRequestManager"
            @updated="fetchData"
          />
        </el-tab-pane>

        <el-tab-pane label="요척" name="yardage">
          <ProductionYardageTab
            ref="yardageTabRef"
            :main-request-id="mainRequestId"
            :can-edit="userStore.isMainRequestManager"
          />
        </el-tab-pane>

        <el-tab-pane label="수량배분" name="color-size">
          <ColorSizeTab
            :main-request-id="mainRequestId"
            :order-quantity="mainRequest?.order_quantity || 0"
            :can-edit="userStore.isMainRequestManager"
            @updated="fetchData"
          />
        </el-tab-pane>

        <el-tab-pane label="그레이딩" name="grading">
          <GradingTab
            ref="gradingTabRef"
            :main-request-id="mainRequestId"
            :can-edit="userStore.isMainRequestManager"
          />
        </el-tab-pane>

        <el-tab-pane label="자재현황" name="materials">
          <MaterialStatusTab
            :main-request-id="mainRequestId"
            :can-edit="userStore.isMainRequestManager"
          />
        </el-tab-pane>

        <el-tab-pane label="생산지시서" name="work-instruction" lazy>
          <WorkInstructionTab v-if="mainRequest" :main-request="mainRequest" />
        </el-tab-pane>

        <el-tab-pane label="임가공계약" name="contracts">
          <ContractTab
            :main-request-id="mainRequestId"
            :can-edit="userStore.isMainRequestManager"
            :order-quantity="mainRequest?.production_quantity || mainRequest?.order_quantity || 0"
          />
        </el-tab-pane>

        <el-tab-pane label="마일스톤" name="milestones">
          <MilestoneTimeline
            :milestones="milestones"
            :can-edit="userStore.isProductionManager"
            :color-options="mrColorOptions"
            :main-request-id="mainRequestId"
            @complete="handleMilestoneComplete"
            @add="openMilestoneForm()"
            @edit="openMilestoneForm"
            @history="openMilestoneHistory"
            @cells-add="handleCellsAdd"
            @bulk-save="handleBulkSave"
          />
        </el-tab-pane>

        <el-tab-pane label="현장보고" name="logs">
          <ProductionLogTab
            :main-request-id="mainRequestId"
            :can-edit="userStore.isProductionManager"
          />
        </el-tab-pane>

        <el-tab-pane label="이슈" name="issues">
          <IssueTab
            :main-request-id="mainRequestId"
            :can-edit="userStore.isMainRequestManager"
          />
        </el-tab-pane>

        <el-tab-pane label="선적/LC" name="shipment">
          <ShipmentTab
            :main-request-id="mainRequestId"
            :can-edit="userStore.isLCManager"
          />
        </el-tab-pane>

        <el-tab-pane v-if="userStore.hasCostAccess" label="원가분석" name="costs">
          <CostBreakdownTab
            :main-request-id="mainRequestId"
            :total-cost="mainRequest?.total_cost"
            :unit-cost="mainRequest?.unit_cost"
            :order-quantity="mainRequest?.order_quantity"
          />
        </el-tab-pane>

        <el-tab-pane label="참여자" name="participants">
          <ParticipantTab
            :participants="participants"
            :can-edit="userStore.isMainRequestManager"
            @updated="fetchParticipants"
          />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 마일스톤 수정/추가 -->
    <el-dialog v-model="msFormVisible" :title="msForm.id ? '마일스톤 수정' : '마일스톤 추가'" width="420px">
      <el-form label-width="90px">
        <el-form-item label="이름">
          <el-input v-model="msForm.name" :disabled="!!msForm.id"
                    placeholder="단계 이름만 (색상은 아래에서 선택)" />
        </el-form-item>
        <!-- 색상 그룹 (2026-08-03): 수량배분 색상에서 선택, 재발주는 'DG#2'처럼 직접 입력 -->
        <el-form-item label="색상">
          <el-select v-model="msForm.colors" multiple filterable allow-create
                     default-first-option style="width:100%"
                     placeholder="비우면 전 색상 공통">
            <el-option v-for="c in mrColorOptions" :key="c" :label="c" :value="c" />
          </el-select>
          <div class="hint" style="line-height:1.4; margin-top:2px">
            재발주 차수는 색상 뒤에 #차수 — 예: DG#2
          </div>
        </el-form-item>
        <el-form-item label="예정일">
          <el-date-picker v-model="msForm.planned_date" type="date"
                          value-format="YYYY-MM-DD" style="width:100%" />
        </el-form-item>
        <el-form-item v-if="msForm.id" label="상태">
          <el-select v-model="msForm.status" style="width:100%">
            <el-option label="대기" value="PENDING" />
            <el-option label="진행중" value="IN_PROGRESS" />
            <el-option label="완료" value="COMPLETED" />
            <el-option label="지연" value="DELAYED" />
            <el-option label="생략" value="SKIPPED" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="msForm.status === 'COMPLETED'" label="완료일">
          <el-date-picker v-model="msForm.actual_date" type="date"
                          value-format="YYYY-MM-DD" style="width:100%" />
        </el-form-item>
        <el-form-item label="비고">
          <el-input v-model="msForm.remarks" type="textarea" :rows="2"
                    placeholder="변경 사유 (예: 공장 회신으로 1주 연기)" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="msFormVisible = false">취소</el-button>
        <el-button type="primary" @click="saveMilestone">저장</el-button>
      </template>
    </el-dialog>

    <!-- 마일스톤 변경 이력 -->
    <el-dialog v-model="msHistoryVisible" :title="`변경 이력 — ${msHistoryTitle}`" width="560px">
      <p v-if="msHistory.auto_origin" class="auto-origin">
        자동 반영: {{ msHistory.auto_origin.source_file }}
        ({{ msHistory.auto_origin.tool }}, {{ formatTs(msHistory.auto_origin.imported_at) }})
      </p>
      <el-table :data="msHistory.logs" size="small" border max-height="360">
        <el-table-column label="일시" width="140">
          <template #default="{ row }">{{ formatTs(row.timestamp) }}</template>
        </el-table-column>
        <el-table-column prop="user" label="누가" width="90" />
        <el-table-column prop="action" label="구분" width="80" />
        <el-table-column label="변경 내용">
          <template #default="{ row }">
            <div v-for="(v, k) in row.changes" :key="k">
              <template v-if="!String(k).startsWith('_')">
                {{ fieldLabel(k) }}: {{ v[0] ?? '없음' }} → {{ v[1] ?? '없음' }}
              </template>
            </div>
            <div v-if="row.changes._source" class="hint-src">원본: {{ row.changes._source }}</div>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!msHistory.logs.length && !msHistory.auto_origin"
                description="기록된 변경 이력이 없습니다" :image-size="60" />
    </el-dialog>

    <!-- 수정 다이얼로그 -->
    <MainRequestFormDialog
      v-model="showEditDialog"
      :edit-data="mainRequest"
      @saved="handleSaved"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/main-request-detail'"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useTabsStore } from '@/stores/tabsStore'
import { ArrowLeft, QuestionFilled, Picture } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import MainRequestFormDialog from '@/components/production/MainRequestFormDialog.vue'
import OverviewTab from '@/components/production/OverviewTab.vue'
import MilestoneTimeline from '@/components/production/MilestoneTimeline.vue'
import CostBreakdownTab from '@/components/production/CostBreakdownTab.vue'
import ShipmentTab from '@/components/production/ShipmentTab.vue'
import ColorSizeTab from '@/components/production/ColorSizeTab.vue'
import ParticipantTab from '@/components/production/ParticipantTab.vue'
import MaterialStatusTab from '@/components/production/MaterialStatusTab.vue'
import ContractTab from '@/components/production/ContractTab.vue'
import ProductionLogTab from '@/components/production/ProductionLogTab.vue'
import ProductionPipeline from '@/components/production/ProductionPipeline.vue'
import GradingTab from '@/components/production/GradingTab.vue'
import WorkInstructionTab from '@/components/production/WorkInstructionTab.vue'
import ProductionYardageTab from '@/components/production/ProductionYardageTab.vue'
import IssueTab from '@/components/production/IssueTab.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const tabsStore = useTabsStore()


// ⚠ keep-alive 함정 픽스 (2026-08-03): App.vue 가 <keep-alive> 로 화면을
// 살려두는데, computed(() => route.params.id) 는 **전역 라우트**를 읽는다.
// 디자인 상세 등 다른 :id 라우트로 이동하는 순간 이 화면(살아있는 상태)의
// id 가 그쪽 id 로 바뀌어 탭들이 엉뚱한 메인오더를 조회했다 — 콘솔 404 폭탄,
// id 가 우연히 겹치면 남의 오더 데이터가 로드되는 위험까지.
// → 이 화면 라우트(/main-requests/:id)에 있을 때만 id 를 갱신한다.
const mainRequestId = ref(route.params.id)
watch(() => route.fullPath, () => {
  if (route.path.startsWith('/main-requests/') && route.params.id
      && route.params.id !== mainRequestId.value) {
    mainRequestId.value = route.params.id
    // keep-alive 재진입으로 다른 오더를 열면 onMounted 가 다시 안 돌므로 재조회
    fetchData()
    fetchMilestones()
    fetchParticipants()
  }
})
const loading = ref(false)
const mainRequest = ref(null)
const milestones = ref([])
const participants = ref([])
// ?tab=milestones 등으로 진입 탭 지정 (스케줄 보드 '마일스톤 미등록 →', 2026-11-04)
const activeTab = ref(route.query.tab || 'overview')
const yardageTabRef = ref(null)
const gradingTabRef = ref(null)
// 탭 진입 시 수량배분 최신값 재조회 — 수량·사이즈 수정 직후에도 파생 화면이 정확 (2026-08-01)
const handleTabChange = (name) => {
  if (name === 'yardage') yardageTabRef.value?.refreshQuantities?.()
  if (name === 'grading') gradingTabRef.value?.refreshSpecs?.()
}
const showEditDialog = ref(false)
const helpVisible = ref(false)

const headerImage = computed(() => {
  const r = mainRequest.value
  if (!r) return ''
  return r.design_front_image || r.instruction_image || r.design_back_image || ''
})

const headerImages = computed(() => {
  const r = mainRequest.value
  if (!r) return []
  return [r.design_front_image, r.design_back_image, r.instruction_image].filter(Boolean)
})

const getStatusType = (status) => {
  const types = {
    'REQUESTED': 'info',
    'MATERIAL_ORDERING': 'warning',
    'IN_PRODUCTION': '',
    'SHIPPING': 'success',
    'DELIVERED': 'success',
    'COMPLETED': 'info',
    'CANCELLED': 'danger'
  }
  return types[status] || 'info'
}

const getDdayType = (days) => {
  if (days === null || days === undefined) return 'info'
  if (days < 0) return 'danger'
  if (days <= 7) return 'warning'
  return 'info'
}

const formatDday = (days) => {
  if (days === null || days === undefined) return '-'
  if (days < 0) return `D+${Math.abs(days)}`
  return `D-${days}`
}

const formatNumber = (num) => {
  return num?.toLocaleString() || '0'
}

const formatCurrency = (amount) => {
  // 원화 표기 (2026-10-17 UAT P6): 기존 `$55650000.00` 표기를 `55,650,000원`으로
  if (amount === null || amount === undefined || amount === '') return '-'
  return `${Math.round(Number(amount)).toLocaleString()}원`
}

const fetchData = async () => {
  loading.value = true
  try {
    const response = await axiosInstance.get(`/production/main-requests/${mainRequestId.value}/`)
    mainRequest.value = response.data
    // 탭 제목에 지시번호(없으면 스타일번호) 부제 표시 — 다중 탭 구분 (2026-08-01)
    const sub = mainRequest.value.request_number || mainRequest.value.style_number
    if (sub) tabsStore.setSubtitle(route.fullPath, sub)
  } catch (error) {
    console.error('Failed to fetch main request:', error)
    ElMessage.error('메인오더 정보를 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

const fetchMilestones = async () => {
  try {
    const response = await axiosInstance.get(`/production/main-requests/${mainRequestId.value}/milestones/`)
    milestones.value = response.data
  } catch (error) {
    console.error('Failed to fetch milestones:', error)
  }
}

const fetchParticipants = async () => {
  try {
    const response = await axiosInstance.get(`/production/main-requests/${mainRequestId.value}/participants/`)
    participants.value = response.data
  } catch (error) {
    console.error('Failed to fetch participants:', error)
  }
}

const handleMilestoneComplete = async (milestone) => {
  try {
    await axiosInstance.post(`/production/milestones/${milestone.id}/complete/`)
    ElMessage.success('마일스톤이 완료되었습니다.')
    fetchMilestones()
  } catch (error) {
    ElMessage.error('마일스톤 완료 처리에 실패했습니다.')
  }
}

// ── 마일스톤 수정/추가/이력 (2026-08-02) ──
const msFormVisible = ref(false)
const msForm = ref({})
const msHistoryVisible = ref(false)
const msHistory = ref({ logs: [], auto_origin: null })
const msHistoryTitle = ref('')

// 색상 옵션 (2026-08-03): 수량배분 색상 + 이미 쓰인 마일스톤 토큰 (차수 포함)
const mrColorOptions = computed(() => {
  const set = new Set()
  ;(mainRequest.value?.colors || []).forEach(c => { if (c.color_name) set.add(c.color_name) })
  ;(milestones.value || []).forEach(m => (m.colors || []).forEach(t => set.add(t)))
  return [...set].sort()
})

const parseNameSuffix = (name) => {
  const m = (name || '').match(/^(.+?)\s*\(([^)]+)\)\s*$/)
  return m ? m[2].split('·').map(t => t.trim()).filter(Boolean) : []
}

const openMilestoneForm = (row = null) => {
  msForm.value = row
    ? { id: row.id, name: row.name, planned_date: row.planned_date,
        colors: (row.colors && row.colors.length) ? [...row.colors] : parseNameSuffix(row.name),
        status: row.status, actual_date: row.actual_date, remarks: row.remarks || '' }
    : { id: null, name: '', planned_date: null, colors: [],
        status: 'PENDING', actual_date: null, remarks: '' }
  msFormVisible.value = true
}

const saveMilestone = async () => {
  const f = msForm.value
  if (!f.name || !f.planned_date) {
    ElMessage.warning('이름과 예정일을 입력하세요.')
    return
  }
  try {
    if (f.id) {
      await axiosInstance.patch(`/production/milestones/${f.id}/`, {
        planned_date: f.planned_date, status: f.status,
        actual_date: f.status === 'COMPLETED' ? f.actual_date : null,
        remarks: f.remarks,
        colors: f.colors || [],   // 서버가 이름 접미사를 자동 동기화
      })
    } else {
      const maxSeq = Math.max(0, ...milestones.value.map(m => m.sequence || 0))
      await axiosInstance.post(`/production/main-requests/${mainRequestId.value}/add_milestone/`, {
        name: f.name, planned_date: f.planned_date, sequence: maxSeq + 1,
        responsible_role: 'MD', remarks: f.remarks,
        colors: f.colors || [],
      })
    }
    ElMessage.success('저장되었습니다.')
    msFormVisible.value = false
    fetchMilestones()
  } catch (e) {
    ElMessage.error('저장에 실패했습니다: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message))
  }
}

// 격자 직접 입력 (2026-08-03): 빈 칸 추가·색상 열 복사 → 일괄 생성
const handleCellsAdd = async (items) => {
  try {
    const { data } = await axiosInstance.post(
      `/production/main-requests/${mainRequestId.value}/add-milestones/`, { items })
    const n = (data.created || []).length
    ElMessage.success(`${n}건 추가되었습니다.`)
    if ((data.skipped || []).length) {
      ElMessage.warning(`이미 있어 건너뜀: ${data.skipped.join(', ')}`)
    }
    fetchMilestones()
  } catch (e) {
    ElMessage.error('추가에 실패했습니다: ' +
      (e.response?.data ? JSON.stringify(e.response.data) : e.message))
  }
}

// 엑셀식 일괄 입력 저장 (2026-08-03): 신규=일괄 API, 기존 날짜 수정=PATCH
const handleBulkSave = async ({ creates, updates, done }) => {
  try {
    let createdN = 0
    if (creates.length) {
      const { data } = await axiosInstance.post(
        `/production/main-requests/${mainRequestId.value}/add-milestones/`,
        { items: creates })
      createdN = (data.created || []).length
      if ((data.skipped || []).length) {
        ElMessage.warning(`이미 있어 건너뜀: ${data.skipped.join(', ')}`)
      }
    }
    for (const u of updates) {
      await axiosInstance.patch(`/production/milestones/${u.id}/`,
        { planned_date: u.planned_date })
    }
    ElMessage.success(`저장 완료 — 신규 ${createdN}건, 수정 ${updates.length}건`)
    await fetchMilestones()
    done?.(true)
  } catch (e) {
    ElMessage.error('저장에 실패했습니다: ' +
      (e.response?.data ? JSON.stringify(e.response.data) : e.message))
    done?.(false)
  }
}

const openMilestoneHistory = async (row) => {
  msHistoryTitle.value = row.name
  msHistory.value = { logs: [], auto_origin: null }
  msHistoryVisible.value = true
  try {
    const res = await axiosInstance.get(`/production/milestones/${row.id}/history/`)
    msHistory.value = res.data
  } catch (e) {
    ElMessage.error('이력 조회에 실패했습니다.')
  }
}

const FIELD_LABELS = {
  planned_date: '예정일', actual_date: '완료일', status: '상태',
  remarks: '비고', name: '이름', assigned_to_id: '담당자', alert_days_before: '사전알림일',
}
const fieldLabel = (k) => FIELD_LABELS[k] || k

const formatTs = (ts) => {
  if (!ts) return ''
  const d = new Date(ts)
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

const goBack = () => {
  router.push('/main-requests')
}

const openEditDialog = () => {
  showEditDialog.value = true
}

const handleSaved = () => {
  showEditDialog.value = false
  fetchData()
}

const STAGE_TAB_MAP = {
  work_order: 'overview',
  material_procurement: 'materials',
  material_export: 'shipment',
  production: 'logs',
  product_import: 'shipment',
  delivery: 'overview',
}

const handleStageClick = (stageKey) => {
  activeTab.value = STAGE_TAB_MAP[stageKey] || 'overview'
}

const showHelp = () => {
  helpVisible.value = true
}

onMounted(() => {
  fetchData()
  fetchMilestones()
  fetchParticipants()
})
</script>

<style scoped>
.main-request-detail {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-left h2 {
  margin: 0;
}

.header-right {
  display: flex;
  gap: 10px;
}

.style-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  padding: 10px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid var(--sw-primary);
}

/* 배너 우측 핵심 지표 (구 요약 카드 대체, 2026-08-01) */
.style-stats {
  display: flex;
  gap: 28px;
  margin-left: auto;
  padding-left: 16px;
  flex-shrink: 0;
}
.style-stats .stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.style-stats .stat-label {
  font-size: 11px;
  color: #909399;
}
.style-stats .stat-value {
  font-size: 15px;
  font-weight: 700;
  color: #303133;
  white-space: nowrap;
}

.style-thumb {
  width: 64px;
  height: 64px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
}

.style-thumb-placeholder {
  width: 64px;
  height: 64px;
  border-radius: 6px;
  border: 1px dashed #dcdfe6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  flex-shrink: 0;
}

.style-info {
  min-width: 0;
}

.style-number-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.style-number {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
}

.style-meta {
  font-size: 13px;
  color: #606266;
}

.meta-sep {
  margin: 0 6px;
  color: #dcdfe6;
}

.dday-tag {
  margin-left: 8px;
}

.production-qty-sub {
  font-size: 14px;
  color: #409eff;
}

.allowance-badge {
  font-size: 11px;
  color: #909399;
  font-weight: normal;
}

.production-pipeline {
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.content-card {
  min-height: 400px;
}

.auto-origin {
  background: #ecf5ff;
  color: #1f3a5f;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 13px;
  margin: 0 0 10px;
}
.hint-src { color: #909399; font-size: 12px; }
.hint { color: #909399; font-size: 12px; }
</style>